import { useGetLabels } from '@ipfabric/api-client/client/model/labels/labels';
import type { GetLabels200Item } from '@ipfabric/api-client/client/schemas/getLabels200Item';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { ListDivider } from '@ipfabric/ui-components/List/ListDivider/ListDivider';
import { ListItem } from '@ipfabric/ui-components/List/ListItem/ListItem';
import { TextInput } from '@ipfabric/ui-components/TextInput/TextInput';
import { Fragment, useState } from 'react';
import { renderQuery } from 'utils/react-query/renderQuery';
import { formatDisplayLabel } from '../formatDisplayLabel';
import { toLabelDataTestIdSuffix } from '../toLabelDataTestIdSuffix';

type LabelPickerProps = {
  onChange: (labels: string[]) => void;
  selectedLabels: string[];
  selectionMode?: 'multiple' | 'single';
};

type LabelPickerRow = {
  key: string;
  label: string;
};

const matchesSearch = (name: string, values: string[], search: string) => {
  const needle = search.trim().toLowerCase();
  if (!needle) {
    return true;
  }

  if (name.toLowerCase().includes(needle)) {
    return true;
  }

  return values.some((value) =>
    formatDisplayLabel(name, value).toLowerCase().includes(needle),
  );
};

const buildLabelPickerRows = (
  catalog: GetLabels200Item[],
  search: string,
): LabelPickerRow[] => {
  const rows: LabelPickerRow[] = [];

  for (const entry of catalog) {
    if (!matchesSearch(entry.name, entry.values, search)) {
      continue;
    }

    if (entry.values.length === 0) {
      rows.push({ key: entry.id, label: entry.name });
      continue;
    }

    for (const value of entry.values) {
      rows.push({
        key: `${entry.id}:${value}`,
        label: formatDisplayLabel(entry.name, value),
      });
    }
  }

  return rows;
};

export const LabelPicker = ({
  onChange,
  selectedLabels,
  selectionMode = 'multiple',
}: LabelPickerProps) => {
  const [search, setSearch] = useState('');
  const labelsQuery = useGetLabels();

  const toggleLabel = (label: string) => {
    if (selectionMode === 'single') {
      onChange(selectedLabels.includes(label) ? [] : [label]);
      return;
    }

    if (selectedLabels.includes(label)) {
      onChange(selectedLabels.filter((item) => item !== label));
      return;
    }

    onChange([...selectedLabels, label]);
  };

  return (
    <Card className='flex flex-col overflow-hidden p-0'>
      <div className='border-b border-default px-sm pb-xs pt-sm shadow-sm'>
        <TextInput
          onChange={(event) => setSearch(event.target.value)}
          placeholder='Search labels'
          startAdornment={<Icon glyph='fa-magnifying-glass' size='sm' />}
          type='text'
          value={search}
        />
      </div>
      {renderQuery({
        query: labelsQuery,
        renderStaleDataOnMount: true,
        success: (catalog) => {
          const rows = buildLabelPickerRows(catalog, search);

          return (
            <div className='flex flex-col py-sm'>
              {rows.map((row, index) => {
                const selected = selectedLabels.includes(row.label);

                return (
                  <Fragment key={row.key}>
                    {index > 0 ? <ListDivider /> : null}
                    <ListItem
                      className='w-full'
                      dataTestId={`assign-labels-label-row-${toLabelDataTestIdSuffix(row.label)}`}
                      endAdornment={
                        selected ? (
                          <Icon glyph='fa-check' size='sm' />
                        ) : undefined
                      }
                      label={row.label}
                      onClick={() => toggleLabel(row.label)}
                      renderAs='button'
                      selected={selected}
                    />
                  </Fragment>
                );
              })}
            </div>
          );
        },
      })}
    </Card>
  );
};
